import * as React from 'react';

import { useQuery } from '@apollo/client';
import {
  LearnerObject_AggregatedAttributeSource as AggregatedAttributeSource,
  LearnerObject_AggregatedAttributeConfidenceClassification as ConfidenceClassification,
} from '__generated__/graphql-types';

import useFetchRolesFromIds from 'bundles/onboarding-common/hooks/useFetchRolesFromIds';
import PersonalizationAggregatedDesiredRolesQuery from 'bundles/personalization-settings/queries/PersonalizationAggregatedDesiredRoles.graphql';
import type {
  PersonalizationAggregatedDesiredRolesQuery as PersonalizationAggregatedDesiredRolesQueryType,
  PersonalizationAggregatedDesiredRolesQueryVariables,
} from 'bundles/personalization-settings/queries/__generated__/PersonalizationAggregatedDesiredRoles';

export type InferredDesiredRole = {
  id: string;
  name: string;
};

export type UseAggregatedDesiredRolesResult = {
  /**
   * The aggregate's inferred roles, resolved to display names and ordered most
   * confident first. Empty when the aggregator promoted nothing inferred, when
   * no aggregate applies, or while the query is still in flight.
   */
  roles: InferredDesiredRole[];
  loading: boolean;
};

/**
 * Orders the confidence buckets so the most confident inferences read first.
 * An item with no classification sorts last: the schema returns null when the
 * winning source has no thresholds configured, which is the absence of a
 * confidence claim rather than a weak one, so it should not outrank a bucket
 * the server actually assigned.
 */
const CONFIDENCE_RANK = {
  [ConfidenceClassification.High]: 0,
  [ConfidenceClassification.Medium]: 1,
  [ConfidenceClassification.Low]: 2,
};

const UNCLASSIFIED_RANK = 3;

const rankOf = (classification: ConfidenceClassification | null | undefined): number =>
  classification == null ? UNCLASSIFIED_RANK : CONFIDENCE_RANK[classification];

type UseAggregatedDesiredRolesParams = {
  /** Skips the aggregate query, so a learner who is not offered the review does not pay for it. */
  skip?: boolean;
};

/**
 * Reads the Aggregator's promoted desired roles for the settings page.
 *
 * Deliberately its own query rather than a field added to
 * `LearnerObjectAttributesInterestedCareers.graphql`: the resolver computes the
 * aggregate lazily, so only callers that select the field pay for it. Onboarding
 * shares that query and has no use for the aggregate, so widening it would hand
 * onboarding the cost of a computation it never reads.
 *
 * Explicit items are dropped. The aggregate is a merged view whose items carry
 * their own provenance, so it also contains roles the learner stated themselves
 * -- those already render in the editor, and repeating them here would present
 * the learner's own answers back to them as inferences.
 *
 * The confidence classification orders the result and nothing else; a learner
 * has no action to take on a confidence bucket, so it is not surfaced visually.
 */
const useAggregatedDesiredRoles = ({
  skip = false,
}: UseAggregatedDesiredRolesParams = {}): UseAggregatedDesiredRolesResult => {
  const { data, loading } = useQuery<
    PersonalizationAggregatedDesiredRolesQueryType,
    PersonalizationAggregatedDesiredRolesQueryVariables
  >(PersonalizationAggregatedDesiredRolesQuery, {
    context: { clientName: 'gatewayGql' },
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
    skip,
  });

  // Stringified so `useFetchRolesFromIds` keeps a stable `roleIds` reference
  // across renders where the aggregate hasn't changed -- it refetches on
  // identity, not value, and would otherwise flash its loading state.
  const inferredIdsString = (data?.LearnerObject?.attributesByUserId?.aggregatedInterestedCareers?.careers ?? [])
    .filter((career) => career.source !== AggregatedAttributeSource.Explicit)
    .slice()
    // Stable within a bucket, so the server's ranked order survives ties.
    .sort((a, b) => rankOf(a.confidenceClassification) - rankOf(b.confidenceClassification))
    .map((career) => career.occupation.id)
    .join(',');

  const inferredIds = React.useMemo(() => (inferredIdsString ? inferredIdsString.split(',') : []), [inferredIdsString]);

  const { roles: resolvedRoles, isLoading: resolveLoading } = useFetchRolesFromIds({ roleIds: inferredIds });

  const roles = React.useMemo<InferredDesiredRole[]>(() => {
    const nameById = new Map(resolvedRoles.map((role) => [role.id, role.name]));
    // Keep the confidence ordering from `inferredIds` -- the catalog fetch
    // returns whatever order the roles service gives back. An id the catalog
    // can't name is dropped rather than rendered as a bare id.
    return inferredIds.flatMap((id) => {
      const name = nameById.get(id);
      return name ? [{ id, name }] : [];
    });
  }, [inferredIds, resolvedRoles]);

  return { roles, loading: loading || (inferredIds.length > 0 && resolveLoading) };
};

export default useAggregatedDesiredRoles;
